import type { ReactElement } from "react";

import { DropdownButton } from "@similarweb/ui-components";

import HooksModalV2Key from "app/constants/HooksModalV2Key";
import { translate } from "app/filters/i18nFilter";
import { CountrySelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/CountrySelectCombobox";
import { FormFieldLabel, RangeSelectWithFilter } from "app/pages/sales-intelligence/common-components/form-fields";
import FilterClearButton from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/FilterClearButton";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import DeviceDropdown from "app/pages/sales-intelligence/modules/apps-search-filters/components/DeviceDropdown/DeviceDropdown";
import {
    COUNTRY_DD_FILTER_ID,
    COUNTRY_SELECT_WIDTH,
    RANGE_SELECT_WIDTH,
} from "app/pages/sales-intelligence/modules/find-apps-filters/constants";
import type { DeviceOption } from "app/pages/sales-intelligence/modules/find-apps-filters/types/commonEnums";
import { formatDisplayedValue } from "app/pages/sales-intelligence/modules/find-apps-filters/utils";
import type {
    IAppsDownloadsAndEngagementFilter,
    IBaseDownloadEngagementFilter,
} from "app/pages/sales-intelligence/store/find-apps/types";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/sub-modules/advanced-search/components/common-styles";
import type { ICountryObject } from "app/services/CountryService";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { openHooksModalV5 } from "app/services/ModalService";

import { StyledContentFilter, StyledDDContainer } from "./styles";

type RangeValueType = number | undefined;

type DownloadEngagementFilterProps<
    TFilter extends IBaseDownloadEngagementFilter<TValue, unknown>,
    TValue extends IAppsDownloadsAndEngagementFilter,
> = IFilterViewBaseProps<TFilter> & {
    hasDeviceFilter?: boolean;
};

const DownloadEngagementFilter = <
    TFilter extends IBaseDownloadEngagementFilter<TValue, unknown>,
    TValue extends IAppsDownloadsAndEngagementFilter,
>({
    filter,
    value,
    onFilterUpdate,
    hasDeviceFilter = false,
}: DownloadEngagementFilterProps<TFilter, TValue>): ReactElement => {
    const renderDDButton = () => {
        return (
            <StyledBaseDropdownButtonWrap key="countries-dropdown-button">
                <DropdownButton>{translate("si.components.countries-dropdown.button.text")}</DropdownButton>
            </StyledBaseDropdownButtonWrap>
        );
    };

    const handleSelect = (country: ICountryObject) => {
        onFilterUpdate({ ...value, country: country.id } as TValue);
    };

    const handleMauChange = ([minValue, maxValue]: RangeValueType[]) => {
        onFilterUpdate({
            ...value,
            monthlyActiveUsers: {
                from: minValue,
                to: maxValue,
            },
        } as TValue);
    };

    const handleMonthlyDownloadsChange = ([minValue, maxValue]: RangeValueType[]) => {
        onFilterUpdate({
            ...value,
            monthlyDownloads: {
                from: minValue,
                to: maxValue,
            },
        } as TValue);
    };

    const clearMauFilters = () => {
        onFilterUpdate({
            ...value,
            monthlyActiveUsers: undefined,
        } as TValue);
    };

    const clearMonthlyDownloadsFilters = () => {
        onFilterUpdate({
            ...value,
            monthlyDownloads: undefined,
        } as TValue);
    };

    const handleDeviceChange = (item: DeviceOption) => {
        onFilterUpdate({
            ...value,
            device: item,
        } as TValue);
    };

    return (
        <StyledContentFilter>
            <FormFieldLabel text={translate("si.find_apps.filters.download-and-engagement.country.label")}>
                <StyledDDContainer
                    id={COUNTRY_DD_FILTER_ID}
                    customWidth={COUNTRY_SELECT_WIDTH}
                >
                    <CountrySelectCombobox
                        onSelect={handleSelect}
                        onRestrictedClick={() => {
                            openHooksModalV5(HooksModalV2Key.WebCountry);
                            return true;
                        }}
                        selectedIds={[value?.country || WORLDWIDE_COUNTRY_ID]}
                        items={filter.countries}
                        renderButton={renderDDButton}
                        searchPlaceHolder={translate("si.find_apps.filters.download-and-engagement.country.placeholder")}
                    />
                </StyledDDContainer>
            </FormFieldLabel>

            {hasDeviceFilter ? (
                <FormFieldLabel text={translate("si.find_apps.filters.download-and-engagement.devices.label")}>
                    <DeviceDropdown
                        options={filter.devices || []}
                        value={value?.device}
                        onChange={handleDeviceChange}
                    />
                </FormFieldLabel>
            ) : null}

            <FormFieldLabel text={translate("si.find_apps.filters.download-and-engagement.downloads.range.label")}>
                <RangeSelectWithFilter
                    fieldName="monthlyDownloads"
                    value={[value?.monthlyDownloads?.from, value?.monthlyDownloads?.to]}
                    optionsMin={filter.minDownloadsOptions}
                    optionsMax={filter.maxDownloadsOptions}
                    placeholderMin={translate("si.find_apps.filters.range.min.placeholder")}
                    placeholderMax={translate("si.find_apps.filters.range.max.placeholder")}
                    onChange={handleMonthlyDownloadsChange}
                    formatValue={formatDisplayedValue}
                    width={RANGE_SELECT_WIDTH}
                >
                    <FilterClearButton
                        isDisabled={!value?.monthlyDownloads}
                        onClick={clearMonthlyDownloadsFilters}
                    />
                </RangeSelectWithFilter>
            </FormFieldLabel>
            <FormFieldLabel text={translate("si.find_apps.filters.download-and-engagement.mau.range.label")}>
                <RangeSelectWithFilter
                    fieldName="monthlyActiveUsers"
                    value={[value?.monthlyActiveUsers?.from, value?.monthlyActiveUsers?.to]}
                    optionsMin={filter.minMauOptions}
                    optionsMax={filter.maxMauOptions}
                    placeholderMin={translate("si.find_apps.filters.range.min.placeholder")}
                    placeholderMax={translate("si.find_apps.filters.range.max.placeholder")}
                    onChange={handleMauChange}
                    formatValue={formatDisplayedValue}
                    width={RANGE_SELECT_WIDTH}
                >
                    <FilterClearButton
                        isDisabled={!value?.monthlyActiveUsers}
                        onClick={clearMauFilters}
                    />
                </RangeSelectWithFilter>
            </FormFieldLabel>
        </StyledContentFilter>
    );
};

export default DownloadEngagementFilter;
